import React, { useState, useEffect, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { 
	Typography, 
	Container, 
	Grid, 
	Card, 
	CardContent, 
	CardActions, 
	Button,
	CircularProgress,
	Box,
	Avatar,
	TextField,
	InputAdornment
} from "@material-ui/core";
import { 
	Business as BusinessIcon, 
	Settings as SettingsIcon,
	Group as UsersIcon,
	LocationOn as LocationIcon,
	PhoneAndroid as DeviceIcon,
	Search as SearchIcon
} from "@material-ui/icons";

import { useTitle } from "../../hooks/useTitle";
import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";
import CompanySettingsModal from "./CompanySettingsModal";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
	root: {
		display: "flex",
		flexDirection: "column",
		padding: theme.spacing(4),
		height: "100%",
		overflowY: "auto",
		...theme.scrollbarStyles,
	},
	headerContainer: {
		marginBottom: theme.spacing(3),
		display: "flex",
		justifyContent: "space-between",
		alignItems: "flex-end",
		flexWrap: "wrap",
		gap: theme.spacing(2)
	},
	companyCard: {
		height: "100%",
		display: "flex",
		flexDirection: "column",
		transition: "transform 0.2s, box-shadow 0.2s",
		border: "1px solid " + theme.palette.divider,
		"&:hover": {
			transform: "translateY(-4px)",
			boxShadow: theme.shadows[4],
		}
	},
	highlightedCard: {
		border: "2px solid " + theme.palette.primary.main,
		backgroundColor: theme.palette.action.hover,
		position: "relative",
	},
	cardContent: {
		flexGrow: 1,
		paddingBottom: theme.spacing(1),
	},
	headerBox: {
		display: "flex",
		alignItems: "center",
		marginBottom: theme.spacing(2),
	},
	avatar: {
		backgroundColor: theme.palette.primary.main,
		width: 50,
		height: 50,
		marginRight: theme.spacing(2),
		boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
	},
	companyName: {
		fontWeight: "bold",
		lineHeight: 1.2,
	},
	locationText: {
		color: theme.palette.text.secondary,
		fontSize: "0.8rem",
		display: "flex",
		alignItems: "center",
		gap: 4,
		marginTop: 4,
	},
	statsBox: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		backgroundColor: theme.palette.action.hover,
		padding: theme.spacing(1.5),
		borderRadius: 8,
		marginTop: theme.spacing(1),
	},
	statItem: {
		display: "flex",
		alignItems: "center",
		gap: 8,
	},
	statValue: {
		fontWeight: "bold",
		fontSize: "1rem",
		lineHeight: 1,
	},
	statLabel: {
		fontSize: "0.7rem",
		color: theme.palette.text.secondary,
		marginTop: 2,
		textTransform: "uppercase",
		letterSpacing: "0.5px"
	},
	separator: {
		height: 24,
		width: 1,
		backgroundColor: theme.palette.divider,
		margin: "0 8px"
	},
	searchInput: {
		minWidth: 300,
	},
	currentLabel: {
		position: "absolute",
		top: 0,
		right: 0,
		backgroundColor: theme.palette.primary.main,
		color: "#fff",
		fontSize: "0.65rem",
		padding: "2px 8px",
		borderBottomLeftRadius: 8,
		fontWeight: "bold",
		textTransform: "uppercase"
	}
}));

const Settings = () => {
	useTitle("Configuración de Compañías");
	const classes = useStyles();
	const { user } = useContext(AuthContext);
	
	const [companies, setCompanies] = useState([]);
	const [loading, setLoading] = useState(false);
	const [selectedCompanyId, setSelectedCompanyId] = useState(null);
	const [modalOpen, setModalOpen] = useState(false);
	const [selectedCompanyCount, setSelectedCompanyCount] = useState(0);
	const [selectedCompanySettings, setSelectedCompanySettings] = useState(null);
	const [searchParam, setSearchParam] = useState("");
	
	const fetchCompanies = async () => {
		setLoading(true);
		try {
			const params = {};
			if (searchParam) {
				params.companyName = searchParam;
			}

			const { data } = await api.get("/company-settings", { params });
			
			const rawList = Array.isArray(data) ? data : (data.data || []);
			
			const formattedCompanies = rawList.map(item => {
				return {
					id: item.id,
					name: item.name,
					city: item.city,
					state: item.state,
					country: item.country,
					logoUrl: item.logoUrl,
					usersCount: item.usersCount,
					settings: item.setting ? {
						id: item.setting.id,
						maxUsers: item.setting.maxUsers,
						maxConnections: item.setting.maxConnections || 3,
						expirationDate: item.setting.expirationDate
					} : null
				};
			});
			
			formattedCompanies.sort((a, b) => {
				if (a.id === user.companyId) return -1;
				if (b.id === user.companyId) return 1;
				return 0;
			});

			setCompanies(formattedCompanies);
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		const delayDebounceFn = setTimeout(() => {
			fetchCompanies();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam]);

	const handleOpenSettings = (companyId, usersCount, settings) => {
		setSelectedCompanyId(companyId);
		setSelectedCompanyCount(usersCount);
		setSelectedCompanySettings(settings);
		setModalOpen(true);
	};

	const handleCloseModal = (shouldRefresh) => {
		setModalOpen(false);
		setSelectedCompanyId(null);
		if (shouldRefresh) {
			fetchCompanies();
		}
	};

	const handleSearch = (event) => {
		setSearchParam(event.target.value);
	};

	const getDaysLeft = (expirationDate) => {
		if (!expirationDate) return null;
		const now = new Date();
		const exp = new Date(expirationDate);
		const diff = exp - now;
		const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
		return days;
	};

	return (
		<div className={classes.root}>
			<Container maxWidth="lg">
				<div className={classes.headerContainer}>
					<div>
						<Typography variant="h5" gutterBottom style={{ fontWeight: 600 }}>
							{i18n.t("settings.title")}
						</Typography>
						<Typography variant="body2" color="textSecondary">
							Administra los límites y configuraciones de cada empresa registrada.
						</Typography>
					</div>
					<TextField
						className={classes.searchInput}
						placeholder="Buscar empresa..."
						type="search"
						variant="outlined"
						size="small"
						value={searchParam}
						onChange={handleSearch}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon color="action" />
								</InputAdornment>
							),
						}}
					/>
				</div>

				{loading ? (
					<Box display="flex" justifyContent="center" p={4}>
						<CircularProgress />
					</Box>
				) : (
					<Grid container spacing={3}>
						{companies.map((company) => {
							const isCurrentCompany = user.companyId === company.id;
							const cardClass = isCurrentCompany 
								? classes.companyCard + " " + classes.highlightedCard 
								: classes.companyCard;

							return (
								<Grid item xs={12} sm={6} md={4} lg={3} key={company.id}>
									<Card className={cardClass} variant="outlined">
										
										{isCurrentCompany && (
											<div className={classes.currentLabel}>TU COMPAÑÍA ACTUAL</div>
										)}

										<CardContent className={classes.cardContent}>
											
											<div className={classes.headerBox}>
												<Avatar 
													src={company.logoUrl} 
													alt={company.name} 
													className={classes.avatar}
												>
													{!company.logoUrl && <BusinessIcon />}
												</Avatar>
												<div style={{ minWidth: 0 }}>
													<Typography variant="subtitle1" className={classes.companyName} noWrap>
														{company.name}
													</Typography>
													<Typography className={classes.locationText} noWrap>
														<LocationIcon style={{ fontSize: 14 }} />
														{company.city}, {company.country}
													</Typography>
												</div>
											</div>

											<div className={classes.statsBox}>
												
												<div className={classes.statItem}>
													<UsersIcon color="action" />
													<div>
														<Typography className={classes.statValue}>
															{company.usersCount} <span style={{ color: "#bdbdbd", fontWeight: 400 }}>/</span> {company.settings ? company.settings.maxUsers : "-"}
														</Typography>
														<Typography className={classes.statLabel}>
															Usuarios
														</Typography>
													</div>
												</div>

												<div className={classes.separator} />
												
												<div className={classes.statItem}>
													<DeviceIcon color="action" />
													<div>
														<Typography className={classes.statValue}>
															{company.settings ? company.settings.maxConnections : "-"}
														</Typography>
														<Typography className={classes.statLabel}>
															Conexiones
														</Typography>
													</div>
												</div>
											</div>
											{company.settings?.expirationDate && (
												<Box mt={2} p={1.5} borderRadius={8} bgcolor={getDaysLeft(company.settings.expirationDate) <= 7 ? "#ffebee" : "#e8f5e9"}>
													<Typography variant="body2" style={{ fontWeight: 600 }}>
														{getDaysLeft(company.settings.expirationDate) > 0
															? `Expira en ${getDaysLeft(company.settings.expirationDate)} días`
															: "Expirada"}
													</Typography>
													<Typography variant="caption" color="textSecondary">
														{new Date(company.settings.expirationDate).toLocaleDateString()}
													</Typography>
												</Box>
											)}
										</CardContent>
										<CardActions style={{ padding: 16, paddingTop: 0 }}>
											<Button 
												size="small" 
												color="primary" 
												onClick={() => handleOpenSettings(company.id, company.usersCount, company.settings)}
												fullWidth
												variant="contained"
												disableElevation
											>
												{company.settings ? "Configurar" : "Crear Configuración"}
											</Button>
										</CardActions>
									</Card>
								</Grid>
							);
						})}
					</Grid>
				)}

				<CompanySettingsModal 
					open={modalOpen}
					onClose={handleCloseModal}
					companyId={selectedCompanyId}
					currentUsersCount={selectedCompanyCount} 
					initialSettings={selectedCompanySettings}
				/>
			</Container>
		</div>
	);
};

export default Settings;